import React, { useMemo, PropsWithChildren } from 'react';
import { ResolvedTopicStrategy, Strategy, Topic } from '@/types';

import { useStrategyQuery } from '../strategy/useStrategyQuery';
import { ServiceDisruption, ServiceDisruptionReasonCode } from './service-disrupted';
import { createUseContext } from '../../../utils/create-use-context';
import { StrategyToggleContextProvider } from './strategy-toggle-context';

export function StrategyLoader({ children }: PropsWithChildren) {
  const { data, status } = useStrategyQuery(false);

  if (status === 'pending') {
    return null;
  }

  if (status === 'error' || data === undefined) {
    return <ServiceDisruption reason={ServiceDisruptionReasonCode.StrategyError} />
  }

  return (
    <StrategyProvider data={data}>
      <StrategyToggleContextProvider>
        { children }
      </StrategyToggleContextProvider>
    </StrategyProvider>
  )
}

type StrategyProviderProps = {
  data: {
    strategiesList: (Strategy & { displayName: string, value: string })[]; // weird
    topics: Topic[];
    resolvedTopicStrategies: Record<string, ResolvedTopicStrategy>;
  }
}

function StrategyProvider({ children, data }: PropsWithChildren<StrategyProviderProps>) {
  const contextValue = useMemo(() => {
    return {
      getStrategyByTopic: (topic: string) => {
        const strategy = data.resolvedTopicStrategies[topic];

        if (!strategy) {
          throw new Error(`Unable to find strategy for topic ${topic}`);
        }

        return strategy;
      },
      getStrategyByStrategyName: (answerStrategyName: string) => {
        const strategy = data.strategiesList.find((strategy) => strategy.answerStrategyName === answerStrategyName);

        if (!strategy) {
          return undefined;
        }

        return strategy;
      },
      /**
       * todo: For now we're returning these values for ease/backwards compatibility
       *  but let's try and remove them
       */
      strategiesList: data.strategiesList,
      topics: data.topics
    }
  }, [data]);

  return (
    <StrategiesContextProvider value={contextValue}>
      { children }
    </StrategiesContextProvider>
  )
}

type ContextValue = {
  getStrategyByTopic: (topic: string) => ResolvedTopicStrategy;
  getStrategyByStrategyName: (answerStrategyName: string) => Strategy|undefined;
  strategiesList: (Strategy & { displayName: string, value: string })[]; // weird
  topics: Topic[];
}

export const StrategiesContext = React.createContext<null | ContextValue>(null);
const StrategiesContextProvider = StrategiesContext.Provider;

export const useStrategyLoaderContext = createUseContext(StrategiesContext);
